/**
 * DanaVoice - Stack Auth Synchronization Service
 * 
 * Frontend service to sync Stack Auth users with backend staff_members table.
 * Implements email whitelist check after authentication.
 */

import { apiClient } from './api-client';

/**
 * User Sync Response Types
 */
interface UserSyncResponse {
  success: boolean;
  error?: {
    code: string;
    message: string;
  };
  data?: {
    staffMemberId: string;
    email: string;
    role: string;
    fullName: string;
    locations: string[];
  };
}

/**
 * Sync Stack Auth user with backend
 * 
 * Called after successful Stack Auth authentication to:
 * 1. Check if user's email is whitelisted in staff_members table
 * 2. If whitelisted, link Stack Auth user ID to staff_members record
 * 3. If not whitelisted, throw error with admin contact message
 * 
 * @param stackAuthUserId - Stack Auth user ID from authentication
 * @param email - User's email address
 * @param displayName - Optional display name from Stack Auth
 * @throws Error if user is not whitelisted or sync fails
 * @returns Staff member data (id, role, etc.)
 */
export async function syncStackAuthUser(
  stackAuthUserId: string,
  email: string,
  displayName?: string
): Promise<UserSyncResponse['data']> {
  try {
    const response = await apiClient.post<UserSyncResponse>('/auth/sync-user', {
      stackAuthUserId,
      email,
      displayName,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to sync user');
    }

    if (!response.data) {
      throw new Error('No user data returned from sync');
    }

    return response.data;
  } catch (error) {
    // Re-throw with user-friendly message
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An error occurred while setting up your account. Please try again.');
  }
}

/**
 * Get user profile from backend using Stack Auth ID
 * 
 * @returns Staff member profile data
 */
export async function getUserProfile() {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data?: {
        id: string;
        email: string;
        fullName: string;
        role: string;
        locations: string[];
        emailNotificationsEnabled: boolean;
        isActive: boolean;
        lastLoginAt: string | null;
        createdAt: string;
      };
      error?: {
        code: string;
        message: string;
      };
    }>('/auth/profile');

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch profile');
    }

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to fetch user profile');
  }
}